'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { Home, ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useParams } from 'next/navigation';
import { isRTLLanguage, type Locale } from '@/lib/i18n/config';
import { Tooth, GumLine } from '@/components/shared/tooth-graphics';

// ─── Main 404 Page ───────────────────────────────────────────────────────────
export default function NotFound() {
  const totalTeeth = 7;
  const missingIndex = 3;
  const { t, locale } = useTranslation('common');
  const params = useParams();
  const currentLocale = (params?.locale as Locale) || locale;
  const isRtl = isRTLLanguage(currentLocale);

  const heading = t('notFound.heading') || 'This Page Has Gone Missing';
  const description = t('notFound.description') || "Every smile has a story — but this page isn't part of ours.";
  const cta = t('notFound.cta') || "Let's get you back on track.";
  const backHome = t('notFound.backHome') || 'Back to Home';
  const goBack = t('notFound.goBack') || 'Go Back';
  const tagline = t('notFound.tagline') || 'Where no smile goes unfinished';

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-20 bg-[#0A1D44] relative overflow-hidden"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
        backgroundSize: '32px 32px',
      }} />

      {/* Gold accent glow */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.12 }}
        transition={{ delay: 1, duration: 1.5 }}
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gold blur-[150px] pointer-events-none"
      />

      <div className="relative z-10 text-center max-w-lg mx-auto">
        {/* 404 Number */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="mb-2"
        >
          <h1
            className="text-[8rem] sm:text-[10rem] font-black leading-none tracking-tighter text-white"
            style={{ fontFamily: 'var(--font-display, var(--font-montserrat, system-ui))' }}
          >
            4
            <motion.span
              className="inline-block text-gold"
              animate={{ rotateZ: [0, -5, 5, -3, 0] }}
              transition={{
                duration: 3,
                repeat: Infinity,
                repeatDelay: 4,
                ease: 'easeInOut',
              }}
            >
              0
            </motion.span>
            4
          </h1>
        </motion.div>

        {/* Teeth row */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="flex items-end justify-center mb-1"
        >
          {Array.from({ length: totalTeeth }).map((_, i) => (
            <Tooth key={i} index={i} isMissing={i === missingIndex} />
          ))}
        </motion.div>

        {/* Gum line */}
        <GumLine />

        {/* Copy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="mt-8 space-y-3"
        >
          <h2
            className="text-xl sm:text-2xl font-semibold text-white"
            style={{ fontFamily: 'var(--font-display, var(--font-montserrat, system-ui))' }}
          >
            {heading}
          </h2>
          <p className="text-white/60 text-sm sm:text-base max-w-sm mx-auto leading-relaxed">
            {description}
            <br />
            {cta}
          </p>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <Link
            href={`/${currentLocale}`}
            className="group inline-flex items-center gap-2.5 px-6 py-3 bg-gold text-white font-medium rounded-lg hover:bg-gold-600 transition-all duration-200 shadow-lg shadow-gold/20 hover:shadow-gold/30"
          >
            <Home className="w-4 h-4" />
            <span>{backHome}</span>
          </Link>
          <button
            onClick={() => {
              if (typeof window !== 'undefined') window.history.back();
            }}
            className="group inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white/60 hover:text-white border border-white/20 rounded-lg hover:border-white/40 transition-all duration-200"
          >
            {isRtl ? (
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
            ) : (
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
            )}
            <span>{goBack}</span>
          </button>
        </motion.div>

        {/* Bottom tagline */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 0.6 }}
          className="mt-16 text-xs text-white/30 tracking-wider uppercase"
          style={{ fontFamily: 'var(--font-display, var(--font-montserrat, system-ui))' }}
        >
          DentaWista &mdash; {tagline}
        </motion.p>
      </div>
    </div>
  );
}
